import type { ReactNode } from 'react';
import { Card } from '@heroui/react';
import { FileText, PenLine } from 'lucide-react';
import type { VisitDetail } from '@/store/visitDetailSlice';
import { formatDateTime } from '@/lib/formatDate';
import { useUserStore } from '@/store/userSlice';

interface VisitInfoCardProps {
  visit: VisitDetail;
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium tracking-wide text-muted uppercase">{label}</span>
      <div className="text-sm text-foreground">{children}</div>
    </div>
  );
}

export function VisitInfoCard({ visit }: VisitInfoCardProps) {
  const isStaff = useUserStore((s) => s.isStaff);
  return (
    <Card className="bg-surface">
      <Card.Header>
        <Card.Title>Информация</Card.Title>
      </Card.Header>
      <Card.Content className="flex flex-col gap-4">
        <InfoRow label="Исполнитель">
          <span className="font-medium">{visit.user.name ?? visit.user.login}</span>
          {visit.user.phone && <div className="text-xs text-muted">{visit.user.phone}</div>}
        </InfoRow>
        <InfoRow label="Шаблон">
          <span className="font-medium">{visit.template.title}</span>
          <div className="text-xs text-muted">{visit.template.slug}</div>
        </InfoRow>
        <InfoRow label="Запланирован">{formatDateTime(visit.plannedAt)}</InfoRow>
        <InfoRow label="Начат">{formatDateTime(visit.startedAt)}</InfoRow>
        <InfoRow label="Завершён">{formatDateTime(visit.completedAt)}</InfoRow>
        {visit.signerName && (
          <InfoRow label="Подпись">
            <span className="flex items-start gap-1.5">
              <PenLine className="mt-0.5 size-4 shrink-0 text-muted" />
              <span>
                {visit.signerName}
                <span className="block text-xs text-muted">{formatDateTime(visit.signedAt)}</span>
              </span>
            </span>
          </InfoRow>
        )}
        {/* PDF визита — только для персонала */}
        {isStaff && visit.pdfUrl && (
          <InfoRow label="Документ">
            <a
              href={visit.pdfUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 text-link hover:underline"
            >
              <FileText className="size-4 shrink-0" />
              PDF-документ
            </a>
          </InfoRow>
        )}
      </Card.Content>
    </Card>
  );
}
